import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';

type IconType = LucideIcon | React.ComponentType<{ className?: string; size?: number }>;
import { cn } from '@utils/utils';
import { SingleScopeGuard, UserScope } from '@feature/auth';
import { useMessageCounter } from '@shared/contexts/MessageCounterContext';

interface NavigationItemProps {
  icon: IconType;
  label: string;
  path: string;
  colorActive: string;
  requiredScope?: UserScope | null;
  isOpen: boolean;
  bullet?: number;
}

export const NavigationItem: React.FC<NavigationItemProps> = ({
  icon: Icon,
  label,
  path,
  colorActive,
  requiredScope,
  isOpen,
  bullet
}) => {
  const location = useLocation();
  const isActive = location.pathname.startsWith(path);
  const { resetUnreadCount } = useMessageCounter();
  const isExternalUrl = path.startsWith('http://') || path.startsWith('https://');

  const handleClick = () => {
    if (path === '/notifications') {
      resetUnreadCount();
    }
  };
  
  const linkClassName = cn(
    "flex items-center text-foreground py-2 rounded-2xl transition-colors relative group bg-primary-60",
    isOpen ? "px-4 gap-2" : "px-2 justify-center",
    !isOpen && bullet !== undefined && bullet > 0 ? "gap-3" : "",
    "hover:border-1 hover:border-white",
    isActive && "border-1 border-white shadow-sm font-medium",
    isActive && "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-8 before:bg-primary before:rounded-r-full"
  );

  const linkStyle = isActive ? { backgroundColor: colorActive } : {};

  const linkProps = {
    onClick: handleClick,
    className: linkClassName,
    style: linkStyle,
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = colorActive;
      }
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = '';
      }
    }
  };

  const linkContent = (
    <>
      <Icon className={cn("h-5 w-5 flex-shrink-0")} />
      {isOpen ? (
        <div className="flex items-center justify-between w-full">
          <span className="transition-opacity duration-300">{label}</span>
          {bullet !== undefined && bullet > 0 && (
            <div className="bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
              {bullet}
            </div>
          )}
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-full ml-2 px-2 py-1 bg-white/90 rounded-md shadow-sm invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200">
            {label}
          </div>
          {bullet !== undefined && bullet > 0 && (
            <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[16px] h-4 flex items-center justify-center">
              {bullet}
            </div>
          )}
        </div>
      )}
    </>
  );

  const navigationLink = isExternalUrl ? (
    <a 
      href={path}
      target="_blank"
      rel="noopener noreferrer"
      {...linkProps}
    >
      {linkContent}
    </a>
  ) : (
    <Link 
      to={path}
      {...linkProps}
    >
      {linkContent}
    </Link>
  );

  if (!requiredScope) {
    return <li>{navigationLink}</li>;
  }

  return (
    <SingleScopeGuard scope={requiredScope}>
      <li>{navigationLink}</li>
    </SingleScopeGuard>
  );
};
